import type { LucideIcon } from 'lucide-react'
import {
  Type,
  Hash,
  Calendar,
  CalendarClock,
  List,
  CheckSquare,
  ToggleLeft,
  AlignLeft,
  PenLine,
  SquareCheckBig,
  Minus,
  Calculator,
  Grid3x3,
  CircleDashed,
  Image as ImageIcon,
  Paperclip,
  Text as TextIcon,
} from 'lucide-react'
import type { CgtFieldType, FieldDefinition } from '@/types/cgt'
import { suggestFieldId } from '@/lib/crossForm'
import type { FieldType, FormField } from './types'

/**
 * Maps a builder FieldType → the CGT field type that gets SAVED.
 *
 * Kept beside its inverse, `toBuilderFieldType`, because the pair has to agree
 * and previously did not: this one lived in `FormDefinitionPanel` and ended in
 * `default: return 'text'`, so an Image field was placed on the canvas, drawn
 * as an Image field, and written to the definition as a TEXT field. Nothing
 * failed anywhere along the way — the rendered form simply showed a text box.
 *
 * **Exhaustive on purpose — do not add a `default` here.** Without it a newly
 * added `FieldType` stops this compiling, which is where that mistake belongs.
 */
export function toCgtFieldType(type: FieldType): CgtFieldType {
  switch (type) {
    case 'select':
      return 'dropdown'
    // The builder offers a Toggle, which is a checkbox wearing different
    // clothes: one boolean, stored identically.
    case 'switch':
      return 'checkbox'
    case 'text':
    case 'textarea':
    case 'checkbox':
    case 'calculated':
    case 'number':
    case 'date':
    case 'datetime':
    case 'signature':
    case 'process':
    case 'section':
    case 'table':
    case 'image':
    case 'attachment':
    case 'description':
    case 'review':
      return type
  }
}

/**
 * Maps a saved CGT field type back to the builder's FieldType.
 *
 * The inverse of `mapFieldType` in `FormDefinitionPanel`, and the ONLY one —
 * `FormEditorPage` had its own copy as a ternary chain, the two drifted, and an
 * Image field opened for editing came back as a text field, which is what the
 * next save then wrote. One function, used by both, cannot drift.
 *
 * **Exhaustive on purpose — do not add a `default` here.** Both copies ended in
 * a `'text'` fallback, so a type with no case was silently downgraded rather
 * than failing anywhere. Without the default, a newly added `CgtFieldType` stops
 * this compiling, which is where that mistake belongs.
 */
export function toBuilderFieldType(fieldType: CgtFieldType): FieldType {
  switch (fieldType) {
    case 'dropdown':
      return 'select'
    case 'text':
    case 'textarea':
    case 'checkbox':
    case 'calculated':
    case 'number':
    case 'date':
    case 'datetime':
    case 'signature':
    case 'process':
    case 'section':
    case 'table':
    case 'image':
    case 'attachment':
    case 'description':
    case 'review':
      return fieldType
  }
}

/**
 * Builds a fresh builder FormField from a saved Field Library definition.
 * Copies the field props, stamps `libraryFieldId` for provenance, and gives the
 * field a brand-new id so it is an independent copy on the form (copy-by-value).
 *
 * Pass `opts.formula` to override the formula (used by the re-map flow so that
 * calculated-field operands are rewritten to match fields on the target form
 * before the field is inserted).
 *
 * Pass `opts.usedIds` (ids already on the target form) so the minted readable
 * id is guaranteed unique on that form; the new id is added to the set.
 */
export function createFieldFromLibrary(
  def: FieldDefinition,
  opts?: { formula?: string; usedIds?: Set<string> },
): FormField {
  return {
    id: suggestFieldId(def.label, opts?.usedIds),
    type: toBuilderFieldType(def.fieldType),
    label: def.label,
    required: def.required,
    placeholder: '',
    helpText: def.helpText ?? '',
    options: (def.options ?? []).map(label => ({ id: crypto.randomUUID(), label })),
    fieldWidth: def.fieldWidth ?? 'half',
    formula: opts?.formula ?? def.formula,
    displayFormat: def.displayFormat,
    requiredGroup: def.requiredGroup,
    allowedSignerGroupIds: def.allowedSignerGroupIds,
    distinctSignerSet: def.distinctSignerSet,
    isGate: def.isGate,
    gatedFormType: def.gatedFormType,
    anchored: def.anchored,
    canChange: def.canChange,
    isMrField: def.isMrField,
    mrFieldKey: def.mrFieldKey,
    libraryFieldId: def.id,
    // Copy-by-value like every other prop — the inserted field owns its own
    // config and later edits to the library row never reach it.
    attachment: def.attachment ? { ...def.attachment } : undefined,
    constantText: def.constantText,
  }
}

export const FIELD_LABELS: Record<FieldType, string> = {
  text: 'Text Input',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & time',
  select: 'Dropdown',
  checkbox: 'Checkbox',
  switch: 'Toggle',
  textarea: 'Textarea',
  signature: 'Signature',
  section: 'Section',
  calculated: 'Calculated',
  table: 'Table',
  process: 'Process',
  image: 'Image',
  attachment: 'Attachment',
  // "Text block", not "Text" or "Description": `Text` is the value-bearing
  // input, and the two must not read as the same thing on the palette
  // (z8nrz7djyy). The type id stays `description` so saved forms load as-is.
  description: 'Text block',
  review: 'Review',
}

export const FIELD_ICONS: Record<FieldType, LucideIcon> = {
  text: Type,
  number: Hash,
  date: Calendar,
  datetime: CalendarClock,
  select: List,
  checkbox: CheckSquare,
  switch: ToggleLeft,
  textarea: AlignLeft,
  signature: PenLine,
  section: Minus,
  calculated: Calculator,
  table: Grid3x3,
  process: CircleDashed,
  image: ImageIcon,
  attachment: Paperclip,
  description: TextIcon,
  review: SquareCheckBig,
}

/**
 * Colour identity for the `process` field type.
 *
 * Process is the only field type that captures no data — it books equipment,
 * consumes materials and costs staff hours, and a person executes it. Without a
 * distinct treatment its canvas card is indistinguishable from a text input.
 *
 * Amber is the app's established accent (the renderer, process tabs and
 * MyProcessSteps all draw from it), so these tokens reuse its exact shades
 * rather than introducing a new ramp. Note amber also marks the PENDING
 * execution status in the renderer — different surface, so a template card and
 * a live step chip do not compete, but keep that in mind before adding more
 * amber to the builder.
 *
 * Split into single-purpose tokens so exactly one border-colour utility applies
 * at a time — relying on Tailwind's emission order to break a tie between two
 * `border-*` classes is a silent breakage waiting to happen.
 */
export const PROCESS_ACCENT = {
  /** Card surface, replacing `bg-card`. */
  surface: 'bg-amber-50 dark:bg-amber-950/30',
  /** Resting border. */
  border: 'border-amber-200 dark:border-amber-800',
  /** Border while hovered (resting state only). */
  borderHover: 'group-hover:border-amber-400',
  /** Border while the card is selected. */
  borderSelected: 'border-amber-500 ring-2 ring-amber-500/25 shadow-sm',
  /** The spine running down the card's left edge. */
  spine: 'bg-amber-500 dark:bg-amber-400',
  /** Icon colour, shared by the canvas preview and the palette entry. */
  icon: 'text-amber-600 dark:text-amber-400',
  /** The inner summary box inside the canvas preview. */
  box: 'border-amber-200 bg-amber-100/50 dark:border-amber-800 dark:bg-amber-900/20',
} as const

/**
 * The five scalar data-entry types a field can be changed between in place
 * (z8nrz7d0h6). Each holds a single value and carries no options list,
 * columns or formula, so changing between them never orphans a property —
 * see `convertibleTypeOptions` for the one excluded pair.
 */
export const CONVERTIBLE_FIELD_TYPES: FieldType[] = ['text', 'number', 'date', 'datetime', 'textarea']

export function isConvertibleFieldType(type: FieldType): boolean {
  return CONVERTIBLE_FIELD_TYPES.includes(type)
}

/**
 * Target options offered for a field currently of a convertible type.
 * Number <-> Date / Date & time is excluded rather than converted — grooming
 * called the pair nonsensical rather than defining a serial-date rule
 * (z8nrz7d0h6 open question 3).
 */
export function convertibleTypeOptions(currentType: FieldType): FieldType[] {
  return CONVERTIBLE_FIELD_TYPES.filter(t => {
    if (currentType === 'number') return t !== 'date' && t !== 'datetime'
    if (currentType === 'date' || currentType === 'datetime') return t !== 'number'
    return true
  })
}

/** Why the field-type control is read-only for a non-convertible field type. */
export const NON_CONVERTIBLE_TYPE_HINT: Partial<Record<FieldType, string>> = {
  select: "Dropdown fields carry an options list that a scalar type can't hold.",
  checkbox: 'Checkbox fields store a true/false value, not a scalar.',
  switch: 'Toggle fields store a true/false value, not a scalar.',
  signature: 'Signature fields record who signed and when, not a scalar value.',
  section: 'Section fields are a layout divider and carry no value.',
  calculated:
    'Calculated fields are derived from a formula — change the formula in the Calculate from panel below instead.',
  table: 'Table fields carry their own grid of columns and rows.',
  process: 'Process fields are configured on the Process tab, not as a scalar value.',
  image: 'Image fields hold an uploaded picture, not a scalar value.',
  attachment: 'Attachment fields hold uploaded files, not a scalar value.',
  description:
    'A text block holds fixed text you write here, not a value the form collects.',
  review: 'Review fields are a sign-off given after the form is completed, not a value.',
}

/**
 * What the palette offers, grouped.
 *
 * `description` is back as "Text block" in Layout (z8nrz7djyy): standing
 * instructions or a static summary that sit with the questions they govern and
 * capture no answer. It was taken off in dc6a731 on the view that a table column
 * covered the need; the requester has since asked for freestanding text, which a
 * section heading (a title) and help text (hidden in a tooltip) do not give.
 * It is also still a table COLUMN type, labelled separately there.
 */
export const PALETTE_GROUPS: { title: string; items: FieldType[] }[] = [
  {
    title: 'Basic',
    items: ['text', 'number', 'date', 'datetime', 'textarea'],
  },
  {
    title: 'Selection',
    items: ['select', 'checkbox', 'switch'],
  },
  {
    title: 'Layout',
    items: ['section', 'description'],
  },
  {
    title: 'Special',
    items: ['signature', 'image', 'attachment', 'calculated', 'table', 'process', 'review'],
  },
]
